/**
 * Refrigerator size calculator — the pure calculation (FRIDGE-356).
 *
 * Originally extracted from app/tools/refrigerator-size-calculator/page-client.tsx
 * so the /tools page and the /embed/size-calculator widget compute the SAME
 * answer from one function. Both callers pass the same three select values
 * held in their state.
 *
 * Fridge.com planning method, as the tool page documents:
 *   base = 4 cu ft per person
 *   cooking:  daily +4 · a few times a week +2 · rarely +0
 *   shopping: weekly +3 · every two weeks +6 · monthly bulk +10 · daily +0
 *   range = [min(base, max(base − 3, 18)), base + 5]
 *   style: <20 compact/top-freezer · <25 standard French door / side-by-side ·
 *          <30 large French door · else extra-large / commercial style
 */
import { readCount } from './numeric-input'

export type CookingFrequency = 'rarely' | 'weekly' | 'daily'
export type ShoppingFrequency = 'daily' | 'weekly' | 'biweekly' | 'monthly'

export interface RefrigeratorSizeInput {
  /**
   * Household size exactly as the visitor typed it (1–20; parsed with the
   * tool's own bounds).
   *
   * Narrowed from `string | number` to `string` in FRIDGE-411. Both callers —
   * app/tools/refrigerator-size-calculator/page-client.tsx and
   * app/embed/_components/size-calculator-widget.tsx — hold this in
   * `useState('4')` and have only ever passed a string, so the number arm was
   * wider than any real use. It mattered once the reader became strict:
   * `readCount` rejects a non-string rather than coercing it, which is right —
   * a pre-parsed number has already lost the distinction between "the visitor
   * typed 0" and "the field was blank".
   */
  householdSize: string
  cookingFrequency: CookingFrequency | string
  shoppingFrequency: ShoppingFrequency | string
}

export interface RefrigeratorSizeResult {
  /** Recommended capacity in cubic feet (the headline figure). */
  recommendedSize: number
  minSize: number
  maxSize: number
  recommendation: string
  household: number
}

/**
 * The inputs the calculator opens with: what /tools/refrigerator-size-calculator
 * and the /embed/size-calculator widget show before a visitor changes anything.
 * lib/refrigerator-answer-facts.ts states the site's family-of-four answer from
 * these same habits, so the number the homepage and /llms.txt print is the one
 * a visitor sees on opening the calculator (FRIDGE-1435).
 */
export const REFRIGERATOR_SIZE_DEFAULTS: Readonly<{
  householdSize: string
  cookingFrequency: CookingFrequency
  shoppingFrequency: ShoppingFrequency
}> = {
  householdSize: '4',
  cookingFrequency: 'daily',
  shoppingFrequency: 'weekly',
}

export const HOUSEHOLD_SIZE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '1', label: '1 person' },
  { value: '2', label: '2 people' },
  { value: '3', label: '3 people' },
  { value: '4', label: '4 people' },
  { value: '5', label: '5 people' },
  { value: '6', label: '6 people' },
]

export const COOKING_FREQUENCY_OPTIONS: Array<{ value: CookingFrequency; label: string }> = [
  { value: 'rarely', label: 'Rarely (eat out mostly)' },
  { value: 'weekly', label: 'Few times a week' },
  { value: 'daily', label: 'Daily cooking' },
]

export const SHOPPING_FREQUENCY_OPTIONS: Array<{ value: ShoppingFrequency; label: string }> = [
  { value: 'daily', label: 'Daily / as needed' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Every 2 weeks' },
  { value: 'monthly', label: 'Monthly bulk shopping' },
]

/**
 * Returns null when the household size does not parse (the tool page then
 * shows its "Enter your household details" prompt).
 */
export function calculateRefrigeratorSize(input: RefrigeratorSizeInput): RefrigeratorSizeResult | null {
  const household = readCount(input.householdSize, { min: 1, max: 20 })
  if (!household) return null

  let baseSize = household * 4 // Starting planning allowance per person
  if (input.cookingFrequency === 'daily') baseSize += 4
  else if (input.cookingFrequency === 'weekly') baseSize += 2
  if (input.shoppingFrequency === 'weekly') baseSize += 3
  else if (input.shoppingFrequency === 'biweekly') baseSize += 6
  else if (input.shoppingFrequency === 'monthly') baseSize += 10

  // The standard-size floor must not exceed a compact recommendation.
  // Otherwise a 4 cu ft recommendation produces an inverted 18–9 cu ft range.
  const minSize = Math.min(baseSize, Math.max(baseSize - 3, 18))
  const maxSize = baseSize + 5

  let recommendation: string
  if (baseSize < 20) recommendation = 'Compact or top-freezer refrigerator'
  else if (baseSize < 25) recommendation = 'Standard French door or side-by-side'
  else if (baseSize < 30) recommendation = 'Large French door refrigerator'
  else recommendation = 'Extra-large French door or commercial style'

  return { recommendedSize: baseSize, minSize, maxSize, recommendation, household }
}
